import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, ChevronRight, ListTree } from "lucide-react";
import { statusDotClasses, statusPillClasses, formatDuration } from "@/lib/format";
import { AvatarGroup } from "@/components/inline/AssigneeInline";
import { toast } from "sonner";

export default function SubtasksSection({ task, list, users, canEdit, onOpenTask, onChange }) {
  const [subtasks, setSubtasks] = useState([]);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const statusMap = Object.fromEntries((list.statuses || []).map((s) => [s.id, s]));

  const refresh = async () => {
    try {
      const { data } = await api.get(`/tasks/${task.id}/subtasks`);
      setSubtasks(data.subtasks);
    } catch { /* ignore */ }
  };

  useEffect(() => { refresh(); }, [task.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setAdding(false); return; }
    setBusy(true);
    try {
      await api.post(`/lists/${list.id}/tasks`, { title: title.trim(), parent_task_id: task.id });
      setTitle(""); setAdding(false);
      await refresh();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add subtask");
    } finally {
      setBusy(false);
    }
  };

  const done = subtasks.filter((s) => {
    const st = statusMap[s.status_id];
    return st && (st.name.toLowerCase() === "done" || st.color === "green");
  }).length;

  return (
    <section data-testid="subtasks-section">
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-1">
          <ListTree className="w-3 h-3" /> Subtasks
          {subtasks.length > 0 && (
            <span className="ml-1 font-mono-data text-neutral-400">({done}/{subtasks.length})</span>
          )}
        </div>
        {canEdit && (
          <Button variant="outline" onClick={() => setAdding(true)} className="h-7 rounded-sm text-xs" data-testid="add-subtask-button">
            <Plus className="w-3 h-3 mr-1" />Add
          </Button>
        )}
      </div>

      <div className="border border-neutral-200 rounded-sm divide-y divide-neutral-100 bg-white">
        {subtasks.length === 0 && !adding && (
          <div className="px-3 py-3 text-xs text-neutral-500 text-center">No subtasks yet.</div>
        )}
        {subtasks.map((s) => {
          const st = statusMap[s.status_id];
          return (
            <button
              key={s.id}
              onClick={() => onOpenTask(s.id)}
              className="w-full grid grid-cols-12 gap-2 px-3 py-2 hover:bg-neutral-50 text-left items-center transition-colors"
              data-testid={`subtask-row-${s.id}`}
            >
              <div className="col-span-7 flex items-center gap-2 min-w-0">
                <ChevronRight className="w-3 h-3 text-neutral-400 shrink-0" />
                <span className={`w-2 h-2 rounded-full shrink-0 ${statusDotClasses(st?.color || "neutral")}`} />
                <span className="text-sm truncate">{s.title}</span>
              </div>
              <div className="col-span-3">
                {st && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${statusPillClasses(st.color)}`}>
                    {st.name}
                  </span>
                )}
              </div>
              <div className="col-span-1">
                <AvatarGroup task={s} users={users} max={2} />
              </div>
              <div className="col-span-1 text-right font-mono-data text-[11px] text-neutral-500">
                {s.total_time_seconds > 0 ? formatDuration(s.total_time_seconds) : "—"}
              </div>
            </button>
          );
        })}
        {adding && (
          <form onSubmit={submit} className="px-3 py-2 bg-neutral-50">
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={(e) => { if (e.target.value.trim()) submit(e); else setAdding(false); }}
              onKeyDown={(e) => { if (e.key === "Escape") { setAdding(false); setTitle(""); } }}
              placeholder="New subtask…"
              disabled={busy}
              className="w-full bg-white px-2 py-1.5 text-sm border border-neutral-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="subtask-input"
            />
          </form>
        )}
      </div>
    </section>
  );
}
